/**
 * Accessibility & preferences context — global, persistent in wallet storage.
 * Applies font size class, contrast, reduced motion to the whole wallet tree.
 *
 * Preferences captured:
 *   - font_size: "sm" | "base" | "lg" | "xl"
 *   - contrast:  "normal" | "high"
 *   - reduce_motion: boolean
 *   - language:  "en" | "zu" | "xh" | "af" | "st" | "tn" | ...
 *   - support_channel: "whatsapp" | "sms" | "email" | "phone" | "branch" | "in_app"
 *   - contact_window:  "any" | "business_hours" | "evenings" | "weekends"
 *   - proactive: boolean
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { loadWallet, updateWallet } from "@/lib/wallet-storage";
import { emitSignal } from "@/lib/wallet-signal";

const AccessibilityCtx = createContext(null);

const DEFAULTS = {
  font_size: "base",
  contrast: "normal",
  reduce_motion: false,
  language: "en",
  support_channel: "email",
  contact_window: "business_hours",
  proactive: true,
};

/** Map any BCP-47 browser locale to one of the wallet's supported languages. */
const SUPPORTED = new Set([
  "en", "af", "zu", "xh", "st", "tn", "nso", "ts", "ss", "ve", "nr",
  "es", "pt", "fr", "sw", "sn",
]);
const BROWSER_ALIASES = {
  // Sesotho aliases (Google Chrome uses `st`, some Firefox versions `sot`)
  sot: "st",
  // Northern Sotho / Sepedi common code
  "nso-za": "nso",
  // Ndebele (South African)
  nd: "nr",
  nde: "nr",
  // Tsonga
  tso: "ts",
  // Venda
  ven: "ve",
  // Swati
  ssw: "ss",
};

function detectBrowserLanguage() {
  if (typeof navigator === "undefined") return null;
  const candidates = [navigator.language, ...(navigator.languages || [])];
  for (const raw of candidates) {
    if (!raw) continue;
    const lower = raw.toLowerCase();
    const primary = lower.split("-")[0];
    if (SUPPORTED.has(lower)) return lower;
    if (BROWSER_ALIASES[lower]) return BROWSER_ALIASES[lower];
    if (SUPPORTED.has(primary)) return primary;
    if (BROWSER_ALIASES[primary]) return BROWSER_ALIASES[primary];
  }
  return null;
}

export function AccessibilityProvider({ children }) {
  // Read from BOTH the accessibility bag AND the top-level wallet.language
  // (onboarding writes to `wallet.language`, Settings writes to both).
  // On first-ever visit — no persisted language anywhere — auto-detect the
  // browser locale and pre-select the closest SA language so NAVI greets
  // the consumer correctly out of the gate.
  const [prefs, setPrefs] = useState(() => {
    const w = loadWallet() || {};
    const stored = w.accessibility?.language || w.language;
    let language = stored;
    let autoDetected = null;
    if (!stored) {
      autoDetected = detectBrowserLanguage();
      language = autoDetected || DEFAULTS.language;
    }
    // Persist the detected language so subsequent components (NaviBubble,
    // NaviIntroModal, etc.) all read the same value.
    if (!stored && autoDetected) {
      const prev = w.accessibility || {};
      updateWallet({
        accessibility: { ...DEFAULTS, ...prev, language: autoDetected },
        language: autoDetected,
        language_source: "browser_detect",
      });
    }
    return {
      ...DEFAULTS,
      ...(w.accessibility || {}),
      language,
    };
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const highContrast = window.matchMedia?.("(prefers-contrast: more)").matches;
    if (reduceMotion || highContrast) {
      setPrefs(p => ({
        ...p,
        reduce_motion: p.reduce_motion || reduceMotion,
        contrast: p.contrast === "normal" && highContrast ? "high" : p.contrast,
      }));
    }
  }, []);

  // Apply classes to <html> so rem-based Tailwind sizes actually scale.
  // Contrast + reduced-motion remain on body (they target Tailwind selectors).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const h = document.documentElement;
    const b = document.body;
    h.classList.remove("wallet-font-sm", "wallet-font-base", "wallet-font-lg", "wallet-font-xl");
    h.classList.add(`wallet-font-${prefs.font_size}`);
    b.classList.toggle("wallet-high-contrast", prefs.contrast === "high");
    b.classList.toggle("wallet-reduce-motion", !!prefs.reduce_motion);
  }, [prefs.font_size, prefs.contrast, prefs.reduce_motion]);

  const update = useCallback((patch) => {
    setPrefs(prev => {
      const next = { ...prev, ...patch };
      // Persist to accessibility bag AND mirror language at top level for
      // components that read from wallet.language directly.
      const mirror = "language" in patch ? { language: patch.language } : {};
      updateWallet({ accessibility: next, ...mirror });
      emitSignal("accessibility.preference.changed.v1", { changed: Object.keys(patch), values: patch });
      return next;
    });
  }, []);

  const value = useMemo(() => ({ prefs, update }), [prefs, update]);
  return <AccessibilityCtx.Provider value={value}>{children}</AccessibilityCtx.Provider>;
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityCtx);
  if (!ctx) return { prefs: DEFAULTS, update: () => {} };
  return ctx;
}
